import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Stack, LinearProgress, Chip, Button, Tooltip, useTheme } from '@mui/material';
import {
  PrecisionManufacturing, Inventory2, AccountBalanceWallet, FactCheck, Memory, Assignment,
  ArrowForwardIos, Circle, WarningAmber, ErrorOutline, InfoOutlined, ChevronRight,
} from '@mui/icons-material';
import { financeWorkspaceService } from '../../services/financeWorkspaceService';
import { formatIndianNumber } from '../../utils/currency';
import DemoStory from '../../components/dashboard/DemoStory';
import { makeControlRoomColors } from '../../components/common/controlRoom';

const fmtVal = (m: any) => {
  if (m.value == null) return '—';
  if (m.money) return formatIndianNumber(m.value);
  return `${m.value}${m.unit || ''}`;
};

const ManufacturingCommandCenter: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const C = makeControlRoomColors(theme);
  const [d, setD] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  const metricMeta: Record<string, { icon: React.ReactNode; color: string }> = {
    production: { icon: <PrecisionManufacturing />, color: C.accent },
    orders: { icon: <Assignment />, color: C.purple },
    cash: { icon: <AccountBalanceWallet />, color: C.good },
    inventory: { icon: <Inventory2 />, color: C.cyan },
    quality: { icon: <FactCheck />, color: C.good },
    machines: { icon: <Memory />, color: C.warn },
  };
  const statusColor = (key: string, v: number | null) => {
    if (v == null) return C.sub;
    if (key === 'machines') return v >= 80 ? C.good : v >= 50 ? C.warn : C.bad;
    if (key === 'production' || key === 'quality') return v >= 90 ? C.good : v >= 75 ? C.warn : C.bad;
    return C.text;
  };
  const sevIcon = (s: string) => s === 'critical' ? <ErrorOutline sx={{ color: C.bad }} /> : s === 'warning' ? <WarningAmber sx={{ color: C.warn }} /> : <InfoOutlined sx={{ color: C.accent }} />;
  const kindDot = (k: string) => ({ invoice: C.good, receipt: C.cyan, production: C.accent, payment: C.warn } as any)[k] || C.sub;

  useEffect(() => {
    financeWorkspaceService.operationsCommandCenter().then(setD).catch(() => setD(null)).finally(() => setLoading(false));
  }, []);

  const metrics = d?.metrics || {};
  const flow = d?.businessFlow || [];
  const attention = d?.attention || [];
  const timeline = d?.timeline || [];

  return (
    <Box sx={{ bgcolor: C.bg, minHeight: '100%', m: -3, p: 0, color: C.text }}>
      {/* Identity bar */}
      <Box sx={{ px: 3, py: 2, borderBottom: `1px solid ${C.line}`, background: `linear-gradient(90deg, ${C.panel}, ${C.bg})`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: C.good, boxShadow: `0 0 10px ${C.good}`, animation: 'pulse 2s infinite' }} />
          <Box>
            <Typography sx={{ fontWeight: 800, fontSize: 20, letterSpacing: 0.3 }}>{d?.identity?.company || 'Operations Command Center'}</Typography>
            <Typography sx={{ color: C.sub, fontSize: 13 }}>{d?.identity?.plant} · {d?.identity?.shift}</Typography>
          </Box>
        </Stack>
        <Stack alignItems="flex-end">
          <Typography sx={{ fontFamily: 'monospace', fontSize: 15, color: C.text }}>{d?.identity?.date}</Typography>
          <Typography sx={{ color: C.sub, fontSize: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Live Operations</Typography>
        </Stack>
        <style>{`@keyframes pulse{0%{opacity:1}50%{opacity:.35}100%{opacity:1}}`}</style>
      </Box>

      {loading && <LinearProgress />}

      {/* Hero metrics */}
      <Box sx={{ px: 3, py: 2.5, display: 'grid', gridTemplateColumns: { xs: 'repeat(2,1fr)', md: 'repeat(3,1fr)', lg: 'repeat(6,1fr)' }, gap: 1.5 }}>
        {Object.entries(metrics).map(([key, m]: any) => {
          const meta = metricMeta[key] || { icon: <Circle />, color: C.accent };
          const col = statusColor(key, m.value);
          return (
            <Box key={key} onClick={() => m.route && navigate(m.route)} sx={{ cursor: 'pointer', bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2, transition: 'all .15s', '&:hover': { borderColor: meta.color, transform: 'translateY(-2px)' } }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.6 }}>{m.label}</Typography>
                <Box sx={{ color: meta.color, opacity: 0.85, '& svg': { fontSize: 18 } }}>{meta.icon}</Box>
              </Stack>
              <Typography sx={{ fontWeight: 800, fontSize: 26, mt: 0.5, color: col, fontFamily: m.money ? 'inherit' : 'monospace' }}>{fmtVal(m)}</Typography>
              {key === 'orders' && m.breakdown && <Typography sx={{ color: C.sub, fontSize: 11 }}>{m.breakdown.sales} sales · {m.breakdown.production} prod · {m.breakdown.purchase} purch</Typography>}
              {key === 'machines' && <Typography sx={{ color: C.sub, fontSize: 11 }}>{m.running} running · {m.down} down</Typography>}
              {key === 'production' && m.planned > 0 && <Typography sx={{ color: C.sub, fontSize: 11 }}>{m.produced} / {m.planned} units</Typography>}
            </Box>
          );
        })}
      </Box>

      {/* Live business flow */}
      <Box sx={{ px: 3, pb: 1 }}>
        <Typography sx={{ color: C.sub, fontSize: 11, textTransform: 'uppercase', letterSpacing: 1, mb: 1 }}>Live Business Flow · click a stage to enter it</Typography>
        <Box sx={{ display: 'flex', alignItems: 'stretch', gap: 0.5, overflowX: 'auto', pb: 1 }}>
          {flow.map((s: any, i: number) => (
            <React.Fragment key={s.key}>
              <Box onClick={() => navigate(s.route)} sx={{ cursor: 'pointer', minWidth: 108, bgcolor: C.panel2, border: `1px solid ${C.line}`, borderRadius: 1.5, px: 1.5, py: 1, textAlign: 'center', transition: 'all .15s', '&:hover': { borderColor: C.accent, bgcolor: C.panel } }}>
                <Typography sx={{ fontSize: 22, fontWeight: 800, color: s.count > 0 ? C.text : C.sub, fontFamily: 'monospace' }}>{s.count}</Typography>
                <Typography sx={{ fontSize: 11, color: C.sub }}>{s.label}</Typography>
              </Box>
              {i < flow.length - 1 && <Box sx={{ display: 'flex', alignItems: 'center', color: C.line }}><ArrowForwardIos sx={{ fontSize: 12 }} /></Box>}
            </React.Fragment>
          ))}
        </Box>
      </Box>

      {/* Demo Story Mode — today's manufacturing story */}
      <Box sx={{ px: 3, pt: 1, pb: 1 }}>
        <DemoStory />
      </Box>

      {/* Two columns: attention + timeline */}
      <Box sx={{ px: 3, py: 2, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1.1fr 1fr' }, gap: 2 }}>
        {/* Attention */}
        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography sx={{ fontWeight: 700, fontSize: 14 }}>Needs Your Attention</Typography>
            <Chip size="small" label={`${attention.length}`} sx={{ bgcolor: attention.length ? C.bad : C.line, color: '#fff', fontWeight: 700 }} />
          </Stack>
          <Stack spacing={1}>
            {attention.map((a: any, i: number) => (
              <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 1, px: 1, borderRadius: 1, bgcolor: C.panel2, border: `1px solid ${C.line}` }}>
                <Box sx={{ '& svg': { fontSize: 20 } }}>{sevIcon(a.severity)}</Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 13, fontWeight: 600, color: C.text }} noWrap>{a.title}</Typography>
                  <Typography sx={{ fontSize: 11, color: C.sub }} noWrap>{a.detail}</Typography>
                </Box>
                {a.action && <Button size="small" endIcon={<ChevronRight />} onClick={() => navigate(a.action.route)} sx={{ color: C.accent, textTransform: 'none', whiteSpace: 'nowrap' }}>{a.action.label}</Button>}
              </Box>
            ))}
            {!loading && !attention.length && <Typography sx={{ color: C.sub, fontSize: 13, py: 2, textAlign: 'center' }}>All clear — no exceptions.</Typography>}
          </Stack>
        </Box>

        {/* Timeline */}
        <Box sx={{ bgcolor: C.panel, border: `1px solid ${C.line}`, borderRadius: 2, p: 2 }}>
          <Typography sx={{ fontWeight: 700, fontSize: 14, mb: 1 }}>Recent Operation</Typography>
          <Box sx={{ position: 'relative', pl: 2, '&:before': { content: '""', position: 'absolute', left: 5, top: 4, bottom: 4, width: 2, bgcolor: C.line } }}>
            {timeline.map((t: any, i: number) => (
              <Box key={i} sx={{ position: 'relative', pb: 1.5 }}>
                <Box sx={{ position: 'absolute', left: -15, top: 4, width: 10, height: 10, borderRadius: '50%', bgcolor: kindDot(t.kind), boxShadow: `0 0 6px ${kindDot(t.kind)}` }} />
                <Typography sx={{ fontSize: 11, color: C.sub, fontFamily: 'monospace' }}>{String(t.ts).slice(0, 16).replace('T', ' ')}</Typography>
                <Typography sx={{ fontSize: 13, color: C.text }}>{t.label}</Typography>
              </Box>
            ))}
            {!loading && !timeline.length && <Typography sx={{ color: C.sub, fontSize: 13 }}>No recent activity.</Typography>}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default ManufacturingCommandCenter;
